/**
 * Centralized constants for all LogRocket performance metric events.
 *
 * Consolidates event names and tunable thresholds in one place to:
 *  - Prevent typo-based silent failures across 20+ event names
 *  - Allow threshold tuning without hunting through business logic
 *  - Serve as a reference catalog for LogRocket dashboard configuration
 */

// ─── Event Names ──────────────────────────────────────────────────────────────

export const METRIC_EVENTS = {
  //  Network & Connectivity
  NETWORK_DIAGNOSTICS: 'Network Diagnostics',
  SIGNALR_RECONNECTING: 'signalr.reconnecting',
  SIGNALR_RECONNECTED: 'signalr.reconnected',
  SIGNALR_CLOSE: 'signalr.close',
  SIGNALR_FATAL: 'signalr.fatal',
  SIGNALR_INVOKE_RTT: 'signalr.invoke_rtt',

  //  Trip Lifecycle
  TRIP_CREATE: 'trip.create',
  TRIP_CREATE_ASSIGN: 'trip.create_assign',
  TRIP_ASSIGN_UNIT: 'trip.assign_unit',
  TRIP_STAMP: 'trip.stamp',

  //  Address Resolution
  ADDRESS_AUTOCOMPLETE: 'address.autocomplete',
  ADDRESS_PLACE_DETAILS: 'address.place_details',
  ADDRESS_GEOCODE: 'address.geocode',
  ADDRESS_GEOCODE_SERVICE: 'address.geocode_service',
  GOOGLE_GEOCODE: 'GoogleGeocode',

  //  Resource List & Units
  RESOURCE_LIST_INITIAL_LOAD: 'resource_list.initial_load',
  RESOURCE_LIST_STATUS_UPDATE_LAG: 'resource_list.status_update_lag',

  //  AVL (Vehicle Positions)
  AVL_HEALTH: 'avl.health',

  //  Pending Assignments
  PENDING_ASSIGNMENTS_COMPUTE: 'pending_assignments.compute',

  //  Trip Search
  TRIP_SEARCH_QUERY: 'trip_search.query',

  //  Map & Zones
  MAP_READY: 'map.ready',
  ZONES_LOAD_DETAILS: 'zones.load_details',

  //  Session Health (periodic)
  SESSION_DEGRADATION: 'session.degradation',
  SESSION_ERROR_DENSITY: 'session.error_density',
  SESSION_THROUGHPUT: 'session.throughput',
  SESSION_HEALTH: 'session.health',

  //  Attachments
  ATTACHMENT_UPLOAD: 'attachment.upload',
  ATTACHMENT_DOWNLOAD: 'attachment.download',
  ATTACHMENT_DELETE: 'attachment.delete',
  ATTACHMENT_VALIDATION_ERROR: 'attachment.validation_error',

  //  Existing (pre-existing events for reference)
  LONG_TASK: 'Long Task',
  INITIAL_UI_READY: 'Initial UI Ready',
  PERF_SUMMARY: 'perf_summary',
  GEOLOCATION: 'geolocation',
} as const;

export type MetricEventName = typeof METRIC_EVENTS[keyof typeof METRIC_EVENTS];

// ─── Thresholds ───────────────────────────────────────────────────────────────

export const METRIC_THRESHOLDS = {
  /**  SignalR invoke RTT: only track invocations slower than this (ms) */
  SIGNALR_INVOKE_SLOW_MS: 1000,

  /** Pending assignments: only emit when computation exceeds this (ms) */
  PENDING_ASSIGNMENTS_SLOW_MS: 20,

  /** Long Task observer: main-thread block threshold (ms) */
  LONG_TASK_THRESHOLD_MS: 400,

  /** Large asset throughput: minimum transfer size to track (bytes) */
  LARGE_ASSET_MIN_BYTES: 100_000,

  /** Status change timing: auto-expiry for unclaimed stamps (ms) */
  STATUS_CHANGE_EXPIRY_MS: 30_000,

  /** Status change timing: max pending entries before purge */
  STATUS_CHANGE_MAX_PENDING: 100,
} as const;

// ─── Timer Intervals ──────────────────────────────────────────────────────────

export const METRIC_INTERVALS = {
  /** Network diagnostics probe interval (ms) */
  NETWORK_DIAGNOSTICS_MS: 5 * 60 * 1000,

  /** AVL health flush interval (ms) */
  AVL_HEALTH_FLUSH_MS: 5 * 60 * 1000,

  /** Session monitor flush interval (ms) */
  SESSION_MONITOR_MS: 10 * 60 * 1000,

  /** Perf telemetry summary flush interval (ms) */
  PERF_TELEMETRY_FLUSH_MS: 10 * 60 * 1000,
} as const;
